package com.gavino.nutritrack

import kotlin.math.abs
import kotlin.math.floor
import kotlin.math.max
import kotlin.math.roundToInt

enum class MealSectionType(val title: String, val icon: String) {
    COLAZIONE("COLAZIONE", "☕"),
    PRANZO("PRANZO", "🍝"),
    CENA("CENA", "🌙"),
    CONDIMENTI("OLIO EVO GIORNALIERO & EXTRA", "🫒")
}

enum class MealSlotType(val label: String) {
    COLAZIONE_BEVANDA("1. Bevanda / Latte / Yogurt"),
    COLAZIONE_CEREALI("2. Quota Biscotti / Fette / Pane"),
    PRIMO("Primo Piatto (Solo Pasta / Riso / Cereali)"),
    CONDIMENTO_PRIMO("Condimento / Sugo per il Primo"),
    SECONDO("Secondo Piatto"),
    CONTORNO("Contorno / Insalatona"),
    PANE("Quota Pane"),
    FRUTTA("Frutta"),
    OLIO_EXTRA("Condimento / Extra")
}

data class SwapOption(
    val category: String,
    val name: String,
    val baseGrams: Int,
    val baseCal: Int,
    val baseCarb: Double,
    val baseProt: Double,
    val baseFat: Double,
    val compNote: String = "",
    val recipeId: String? = null
)

data class SwapGroup(
    val id: String,
    val title: String,
    val options: List<SwapOption>
)

data class DietRecipe(
    val id: String,
    val dayReference: String,
    val title: String,
    val ingredients: String,
    val preparation: String
)

data class FoodItem(
    val id: String,
    val name: String,
    val slotType: MealSlotType,
    val baseGrams: Int,
    val currentGrams: Int = baseGrams,
    val baseCal: Int,
    val baseCarb: Double,
    val baseProt: Double,
    val baseFat: Double,
    val compNote: String = "",
    val swapGroupId: String? = null,
    val recipeId: String? = null,
    // --- CAMPI COSTANTI DEL DIETOLOGO (SEMPRE VISIBILI ANCHE DOPO SOSTITUZIONE) ---
    val originalPrescribedName: String = name,
    val originalPrescribedGrams: Int = baseGrams,
    val originalPrescribedCal: Int = baseCal,
    val originalCompNote: String = compNote,
    // --- DATI CONTACALORIE REALE (NON MODIFICANO LA DIETA) ---
    val isConsumed: Boolean = false,
    val consumedGrams: Int = currentGrams,
    val isExtraItem: Boolean = false,
    val isAddedFromCatalog: Boolean = false
) {
    private val prescribedRatio: Double
        get() = if (baseGrams > 0) currentGrams.toDouble() / baseGrams.toDouble() else 1.0

    val currentCal: Int get() = (baseCal * prescribedRatio).roundToInt()
    val currentCarb: Double get() = ((baseCarb * prescribedRatio) * 10.0).roundToInt() / 10.0
    val currentProt: Double get() = ((baseProt * prescribedRatio) * 10.0).roundToInt() / 10.0
    val currentFat: Double get() = ((baseFat * prescribedRatio) * 10.0).roundToInt() / 10.0

    private val consumedRatio: Double
        get() = if (baseGrams > 0) consumedGrams.toDouble() / baseGrams.toDouble() else 1.0

    val eatenCal: Int get() = if (isConsumed) (baseCal * consumedRatio).roundToInt() else 0
    val eatenCarb: Double get() = if (isConsumed) ((baseCarb * consumedRatio) * 10.0).roundToInt() / 10.0 else 0.0
    val eatenProt: Double get() = if (isConsumed) ((baseProt * consumedRatio) * 10.0).roundToInt() / 10.0 else 0.0
    val eatenFat: Double get() = if (isConsumed) ((baseFat * consumedRatio) * 10.0).roundToInt() / 10.0 else 0.0

    val isScaled: Boolean get() = abs(currentGrams - baseGrams) >= 1
    val isSwappedFromOriginal: Boolean get() = !isExtraItem && name != originalPrescribedName

    fun scaleByFactor(factor: Double): FoodItem {
        if (isExtraItem) return this
        val newPrescribed = if (abs(factor - 1.0) < 0.01) {
            baseGrams
        } else {
            val maxAllowedCal = (originalPrescribedCal * factor).roundToInt().coerceAtLeast(5)
            val calPerGram = if (baseGrams > 0) baseCal.toDouble() / baseGrams.toDouble() else 1.0
            smartRoundGramsCapped(baseGrams * factor, calPerGram, maxAllowedCal)
        }
        return copy(
            currentGrams = newPrescribed,
            consumedGrams = if (isConsumed) consumedGrams else newPrescribed
        )
    }

    /**
     * Calcola l'anteprima esatta (grammi, calorie e macro) per il popup delle sostituzioni (Base o Smart AI),
     * agganciandosi SEMPRE alle calorie previste dallo slot corrente senza mai sforarle!
     */
    fun previewOptionForCurrentSlot(opt: SwapOption): SwapOption {
        val targetSlotCal = currentCal.coerceAtLeast(10)
        val optGrams = opt.baseGrams.toDouble().coerceAtLeast(1.0)
        val optCal = opt.baseCal.toDouble().coerceAtLeast(1.0)
        val calPerGram = optCal / optGrams

        val rawGrams = targetSlotCal.toDouble() / calPerGram
        val exactGrams = smartRoundGramsCapped(rawGrams, calPerGram, targetSlotCal)
        val ratio = exactGrams.toDouble() / optGrams

        val calcCal = (optCal * ratio).roundToInt()
        val calcCarb = ((opt.baseCarb * ratio) * 10.0).roundToInt() / 10.0
        val calcProt = ((opt.baseProt * ratio) * 10.0).roundToInt() / 10.0
        val calcFat = ((opt.baseFat * ratio) * 10.0).roundToInt() / 10.0

        val cleanedCat = stripTrailingGrams(opt.category)
        val cleanedName = scaleOrStripGramsInName(opt.name, ratio)

        return opt.copy(
            category = cleanedCat,
            name = cleanedName,
            baseGrams = exactGrams,
            baseCal = calcCal,
            baseCarb = calcCarb,
            baseProt = calcProt,
            baseFat = calcFat
        )
    }

    /**
     * Applica la sostituzione creando un FoodItem con baseGrams/baseCal (100% dietologo)
     * e currentGrams (scalati sul target attuale) perfettamente allineati, così currentCal non raddoppia MAI!
     */
    fun applySwappedOption(opt: SwapOption): FoodItem {
        val targetSlotCal = currentCal.coerceAtLeast(10)
        val unscaledSlotCal = originalPrescribedCal.coerceAtLeast(10)

        val optGrams = opt.baseGrams.toDouble().coerceAtLeast(1.0)
        val optCal = opt.baseCal.toDouble().coerceAtLeast(1.0)
        val calPerGram = optCal / optGrams

        // 1. Grammi per la base 100% del dietologo (originalPrescribedCal)
        val unscaledGrams = smartRoundGramsCapped(
            rawValue = unscaledSlotCal.toDouble() / calPerGram,
            calPerGram = calPerGram,
            maxAllowedCal = unscaledSlotCal
        )
        val baseRatio = unscaledGrams.toDouble() / optGrams
        val newBaseCal = (optCal * baseRatio).roundToInt().coerceAtMost(unscaledSlotCal)
        val newBaseCarb = ((opt.baseCarb * baseRatio) * 10.0).roundToInt() / 10.0
        val newBaseProt = ((opt.baseProt * baseRatio) * 10.0).roundToInt() / 10.0
        val newBaseFat = ((opt.baseFat * baseRatio) * 10.0).roundToInt() / 10.0

        // 2. Grammi per lo slot attuale (targetSlotCal)
        val currentRatioToUnscaled = targetSlotCal.toDouble() / unscaledSlotCal.toDouble()
        val newCurrentGrams = if (abs(currentRatioToUnscaled - 1.0) < 0.01) {
            unscaledGrams
        } else {
            val effectiveCalPerGram = if (unscaledGrams > 0) newBaseCal.toDouble() / unscaledGrams.toDouble() else calPerGram
            smartRoundGramsCapped(
                rawValue = targetSlotCal.toDouble() / effectiveCalPerGram,
                calPerGram = effectiveCalPerGram,
                maxAllowedCal = targetSlotCal
            )
        }

        val displayRatio = newCurrentGrams.toDouble() / optGrams
        val cleanedName = if (opt.name.startsWith("Mix (")) opt.name else scaleOrStripGramsInName(opt.name, displayRatio)
        val finalCal = (newBaseCal * (newCurrentGrams.toDouble() / unscaledGrams.toDouble().coerceAtLeast(1.0))).roundToInt()

        val noteText = if (opt.name.startsWith("Mix (") && opt.compNote.isNotEmpty()) {
            opt.compNote
        } else {
            "Dose calcolata: ${newCurrentGrams}g (${finalCal} kcal su ${targetSlotCal} kcal previste)"
        }

        return copy(
            name = cleanedName,
            baseGrams = unscaledGrams,
            currentGrams = newCurrentGrams,
            consumedGrams = newCurrentGrams,
            baseCal = newBaseCal,
            baseCarb = newBaseCarb,
            baseProt = newBaseProt,
            baseFat = newBaseFat,
            compNote = noteText,
            recipeId = opt.recipeId ?: recipeId
        )
    }

    companion object {
        fun smartRoundGrams(value: Double): Int {
            if (value <= 0.0) return 0
            return if (value < 40.0) {
                max(1, value.roundToInt())
            } else {
                max(5, ((value / 5.0).roundToInt() * 5))
            }
        }

        /**
         * Arrotonda i grammi ma garantisce che le calorie risultanti NON superino mai maxAllowedCal
         */
        fun smartRoundGramsCapped(rawValue: Double, calPerGram: Double, maxAllowedCal: Int): Int {
            if (rawValue <= 0.0) return 0
            var candidate = smartRoundGrams(rawValue)
            while (candidate > 1 && (candidate * calPerGram).roundToInt() > maxAllowedCal) {
                candidate = if (candidate > 45) candidate - 5 else candidate - 1
            }
            return max(1, candidate)
        }

        private val trailingGramsRegex = Regex("""\s*\(\d+\s*g\)\s*$""", RegexOption.IGNORE_CASE)
        private val inlineGramsRegex = Regex("""\((\d+)\s*g\)""", RegexOption.IGNORE_CASE)

        fun stripTrailingGrams(text: String): String {
            return text.replace(trailingGramsRegex, "").trim()
        }

        /**
         * Rimuove l'etichetta finale fissa es. "(300g)" dal nome del piatto;
         * se invece ci sono più sotto-ingredienti es. "Fette (30g) + Marmellata (10g)", scala anche quelli!
         */
        fun scaleOrStripGramsInName(rawName: String, ratio: Double): String {
            val withoutTrailing = rawName.replace(trailingGramsRegex, "").trim()
            if (!inlineGramsRegex.containsMatchIn(withoutTrailing)) {
                return withoutTrailing
            }
            return inlineGramsRegex.replace(withoutTrailing) { matchResult ->
                val origG = matchResult.groupValues[1].toIntOrNull() ?: return@replace matchResult.value
                val scaledSubG = smartRoundGrams(origG * ratio)
                "(${scaledSubG}g)"
            }
        }

        /**
         * Scala l'intera giornata sul targetKcal scelto dall'utente (es. 1300 kcal da TDEE),
         * garantendo che la somma totale del giorno NON superi mai targetKcal nemmeno di 1 kcal!
         */
        fun scaleDayMealsToExactTarget(
            meals: Map<MealSectionType, List<FoodItem>>,
            dayBaseTotalCal: Int,
            targetKcal: Int
        ): Map<MealSectionType, List<FoodItem>> {
            if (dayBaseTotalCal <= 0) return meals
            val factor = targetKcal.toDouble() / dayBaseTotalCal.toDouble()

            val scaledMap = meals.mapValues { (_, list) ->
                list.map { it.scaleByFactor(factor) }.toMutableList()
            }.toMutableMap()

            // Verifica che la somma delle portate (non extra) non superi mai targetKcal
            var totalPrescribed = scaledMap.values.flatten().filter { !it.isExtraItem }.sumOf { it.currentCal }
            var safetyCounter = 0
            while (totalPrescribed > targetKcal && safetyCounter < 40) {
                safetyCounter++
                // Riduci di 1-5g l'alimento più calorico del giorno finché il totale è <= targetKcal
                var maxSection: MealSectionType? = null
                var maxIndex = -1
                var maxCal = -1
                scaledMap.forEach { (sec, list) ->
                    list.forEachIndexed { idx, item ->
                        if (!item.isExtraItem && item.currentGrams > 10 && item.currentCal > maxCal) {
                            maxCal = item.currentCal
                            maxSection = sec
                            maxIndex = idx
                        }
                    }
                }
                val sec = maxSection ?: break
                val item = scaledMap[sec]!![maxIndex]
                val step = if (item.currentGrams > 50) 5 else 1
                val newG = (item.currentGrams - step).coerceAtLeast(5)
                scaledMap[sec]!![maxIndex] = item.copy(
                    currentGrams = newG,
                    consumedGrams = if (item.isConsumed) item.consumedGrams else newG
                )
                totalPrescribed = scaledMap.values.flatten().filter { !it.isExtraItem }.sumOf { it.currentCal }
            }

            return scaledMap
        }
    }
}

data class DietPlanDay(
    val dayNum: Int,
    val label: String,
    val tag: String,
    val summaryPranzo: String,
    val summaryCena: String,
    val baseOilGrams: Int,
    val meals: Map<MealSectionType, List<FoodItem>>
) {
    val baseTotalCal: Int
        get() = meals.values.flatten().sumOf { it.baseCal }

    val baseTotalCarb: Int
        get() = meals.values.flatten().sumOf { it.baseCarb }.roundToInt()

    val baseTotalProt: Int
        get() = meals.values.flatten().sumOf { it.baseProt }.roundToInt()

    val baseTotalFat: Int
        get() = meals.values.flatten().sumOf { it.baseFat }.roundToInt()
}

data class WeightEntry(
    val id: String,
    val dateLabel: String,
    val weightKg: Double,
    val waistCm: Int,
    val note: String = ""
)

/**
 * Gestore Calendario Settimanale (Lunedì -> Domenica):
 * Calcola esattamente il Lunedì della settimana corrente e verifica se una data "dd/MM"
 * appartiene alla settimana in corso (così G5 segnato Venerdì resta salvato fino a Domenica notte
 * e si azzera SOLO quando scatta il nuovo Lunedì!).
 */
object WeeklyCalendarHelper {

    private fun isLeapYear(year: Int): Boolean =
        (year % 4 == 0 && year % 100 != 0) || (year % 400 == 0)

    private fun daysInMonth(month: Int, year: Int): Int = when (month) {
        1, 3, 5, 7, 8, 10, 12 -> 31
        4, 6, 9, 11 -> 30
        2 -> if (isLeapYear(year)) 29 else 28
        else -> 30
    }

    // Restituisce 0 = Lunedì, 1 = Martedì ... 6 = Domenica (Algoritmo di Sakamoto)
    private fun dayOfWeekMondayZero(day: Int, month: Int, year: Int): Int {
        val t = intArrayOf(0, 3, 2, 5, 0, 3, 5, 1, 4, 6, 2, 4)
        var y = year
        if (month < 3) y -= 1
        val dowSunZero = (y + y / 4 - y / 100 + y / 400 + t[month - 1] + day) % 7
        return (dowSunZero + 6) % 7
    }

    private fun shiftDate(day: Int, month: Int, year: Int, deltaDays: Int): Triple<Int, Int, Int> {
        var d = day
        var m = month
        var y = year
        var rem = deltaDays
        while (rem > 0) {
            d++
            if (d > daysInMonth(m, y)) {
                d = 1
                m++
                if (m > 12) { m = 1; y++ }
            }
            rem--
        }
        while (rem < 0) {
            d--
            if (d < 1) {
                m--
                if (m < 1) { m = 12; y-- }
                d = daysInMonth(m, y)
            }
            rem++
        }
        return Triple(d, m, y)
    }

    private fun formatDdMm(day: Int, month: Int): String =
        "${day.toString().padStart(2, '0')}/${month.toString().padStart(2, '0')}"

    /**
     * Restituisce la chiave univoca del Lunedì della settimana corrente (es. "MON_2026_12/10")
     * e l'elenco delle 7 date valide (Lunedì..Domenica) della settimana corrente.
     */
    fun getCurrentWeekMondayKeyAndDates(todayDdMm: String, year: Int = 2026): Pair<String, Set<String>> {
        val parts = todayDdMm.split("/")
        val day = parts.getOrNull(0)?.toIntOrNull() ?: 1
        val month = parts.getOrNull(1)?.toIntOrNull() ?: 1

        val offsetFromMonday = dayOfWeekMondayZero(day, month, year) // 0=Lun .. 6=Dom
        val (monD, monM, monY) = shiftDate(day, month, year, -offsetFromMonday)
        val mondayKey = "MON_${monY}_${formatDdMm(monD, monM)}"

        val validDates = mutableSetOf<String>()
        for (i in 0..6) {
            val (curD, curM, _) = shiftDate(monD, monM, monY, i)
            validDates.add(formatDdMm(curD, curM))
        }
        return mondayKey to validDates
    }
}